/**
 * Workspace — the docked-panel shell.
 *
 *   ┌──────────────── command bar ────────────────┐
 *   │ Navigator │        view        │    Info    │
 *   │  (left)   │     (center)       │  (right)   │
 *   └──────────────── status bar ─────────────────┘
 *
 * The center view maximizes; both docks collapse, resize by dragging their
 * gutters, and remember their state per viewer in localStorage. The right
 * dock is the Info panel: pinned (in-flow) or floating (over the view).
 * Nothing here knows about domains; the router calls setContext() and the
 * Navigator tree is handed in from the registry.
 */

import { listDomains, listCommunityTypes, NETWORK, PLATFORM, STATUS_LABEL, type PhaseStatus } from '../lib/DomainRegistry.js';
import { SETUP_EVENT } from '../lib/ProjectSetup.js';
import { LEVEL_LABEL, hasProjectInputs, resolveAll, type Applicability } from '../lib/SystemsHierarchy.js';
import { activeSetup, currentProjectId, projectStore, type Cue, type CueItem } from '../lib/ProjectContainer.js';
import { InfoPanel } from './InfoPanel.js';

export type RightMode = 'pinned' | 'floating' | 'hidden';

export interface WorkspaceContext {
    service: string;
    domain?: string | null;
    hint?: string;
}

interface NavNode {
    id: string;
    label: string;
    depth: number;
    service?: string;
    section?: string;
    status?: PhaseStatus;
    /** Applicability for the current project (systems hierarchy); shown instead of status when a project is set up. */
    level?: Applicability;
    reason?: string;
    swatch?: string;
    icon?: string;
    children?: NavNode[];
    /** Children start hidden (the Library under a project). */
    collapsed?: boolean;
    /** A free pill, when neither status nor level fits (e.g. "bound", "captured"). */
    pill?: { text: string; status: PhaseStatus };
    /** Marks a cue item so the tree can style "now" differently. */
    kind?: 'now' | 'phase' | 'deliverable';
}

const STORE_KEY = 'ws.layout.v1';
const MIN_LEFT = 160, MAX_LEFT = 420, MIN_RIGHT = 260, MAX_RIGHT = 640;

interface LayoutState { left: 'open' | 'collapsed'; right: RightMode; leftW: number; rightW: number; }

function loadState(): LayoutState {
    const base: LayoutState = { left: 'open', right: 'pinned', leftW: 240, rightW: 360 };
    try {
        const raw = localStorage.getItem(STORE_KEY);
        if (raw) return { ...base, ...JSON.parse(raw) };
    } catch { /* private mode or blocked storage */ }
    // Narrow windows: docks overlay the view, so start with them closed.
    if (typeof window !== 'undefined' && window.innerWidth < 1100) base.right = 'hidden';
    if (typeof window !== 'undefined' && window.innerWidth <= 900) base.left = 'collapsed';  // matches the CSS max-width: 900px breakpoint
    return base;
}

export class Workspace {
    readonly topbar: HTMLElement;
    readonly center: HTMLElement;
    readonly info: InfoPanel;
    /** The dock row; overlays that should leave the command bar usable mount here. */
    readonly main: HTMLElement;
    private left: HTMLElement;
    private right: HTMLElement;
    private leftBody: HTMLElement;
    private gutterL: HTMLElement;
    private gutterR: HTMLElement;
    private status: HTMLElement;
    private state: LayoutState = loadState();
    private selectedNode: string | null = null;
    private tree: NavNode[] = [];
    private onNavigate: (service: string) => void = () => {};

    constructor(app: HTMLElement) {
        document.body.classList.add('ws-body');
        app.innerHTML = '';
        app.classList.add('ws');

        this.topbar = el('nav', 'ws-topbar');
        this.main = el('div', 'ws-main');
        this.left = el('aside', 'ws-dock ws-dock-left');
        this.gutterL = el('div', 'ws-gutter ws-gutter-left');
        this.center = el('main', 'ws-center');
        this.gutterR = el('div', 'ws-gutter ws-gutter-right');
        this.right = el('aside', 'ws-dock ws-dock-right');
        this.status = el('footer', 'ws-status');

        const service = el('div', '');
        service.id = 'service-container';
        this.center.appendChild(service);

        // Left dock chrome
        const lh = el('div', 'ws-dock-head');
        lh.innerHTML = `<span>Navigator</span><span class="ws-spacer"></span>
            <button class="ws-icon-btn" id="ws-left-collapse" title="Collapse (Ctrl+B)"><i class="fa-solid fa-angles-left"></i></button>`;
        this.leftBody = el('div', 'ws-dock-body');
        this.left.append(lh, this.leftBody);

        // Right dock chrome + InfoPanel
        const rh = el('div', 'ws-dock-head');
        rh.innerHTML = `<span>Info</span><span class="ws-spacer"></span>
            <button class="ws-icon-btn" id="ws-right-pin" title="Pin to the right (in-flow) / float over the view"><i class="fa-solid fa-thumbtack"></i></button>
            <button class="ws-icon-btn" id="ws-right-close" title="Close (F1)"><i class="fa-solid fa-xmark"></i></button>`;
        const rb = el('div', 'ws-dock-body');
        this.info = new InfoPanel(rb);
        this.right.append(rh, rb);

        this.main.append(this.left, this.gutterL, this.center, this.gutterR, this.right);
        app.append(this.topbar, this.main, this.status);

        this.renderStatus({ service: 'overview' });
        this.wireChrome();
        this.applyState();
        // The Navigator's Systems branches carry applicability for the current project; rebuild when it changes.
        window.addEventListener(SETUP_EVENT, () => { if (this.tree.length) this.buildNavigator(); });
        window.addEventListener('storage', (e) => { if (e.key && e.key.startsWith('mokulab.projectSetup') && this.tree.length) this.buildNavigator(); });
    }

    // ── Public API ────────────────────────────────────────────────────

    /** Called by the router after each render; updates crumbs, tree selection, and the Info panel. */
    setContext(ctx: WorkspaceContext): void {
        this.info.setDomain(ctx.domain ?? null);
        this.renderStatus(ctx);
        this.selectNode(ctx.domain ? `domain:${ctx.domain}` : 'root');
    }

    setNavigateHandler(fn: (service: string) => void): void { this.onNavigate = fn; }

    /**
     * Build the Navigator. With a container open it starts at "now": the project, the
     * cue for the current phase, setup, the basis of design, the phase ladder (past
     * collapsed to a line, current open with its owed evidence and the systems it
     * decides, next as a line, later hidden), deliverables, and the Library collapsed.
     * Without one: Start, then the Library (the registry tree as before).
     */
    buildNavigator(): void {
        const project = projectStore.project;
        const cue = projectStore.cue;
        const library = this.libraryNodes(2);
        const libraryNode: NavNode = { id: 'library', label: 'Library', depth: 1, icon: 'fa-book', collapsed: true, children: library, reason: 'The systems and families the lab offers, on demand; what a project calls for is decided from its interview' };
        if (project && cue) {
            this.tree = [this.projectTree(project.name, project.status, cue, library)];
        } else if (currentProjectId() && (projectStore.loading || projectStore.error)) {
            const root: NavNode = { id: 'root', label: PLATFORM.name, depth: 0, service: 'overview', icon: 'fa-flask', children: [
                { id: 'project:loading', label: projectStore.error ? `Project: ${projectStore.error}` : 'Opening the project…', depth: 1, service: 'start', icon: projectStore.error ? 'fa-triangle-exclamation' : 'fa-spinner' },
                libraryNode,
            ] };
            this.tree = [root];
        } else {
            // No container: the interview is the way in. The registry tree stays one collapsed node.
            const root: NavNode = { id: 'root', label: PLATFORM.name, depth: 0, service: 'overview', icon: 'fa-flask', children: [
                { id: 'start', label: 'Start a project', depth: 1, service: 'start', icon: 'fa-circle-play', reason: 'Start a project, upload a scheme, or open one you hold' },
                libraryNode,
            ] };
            this.tree = [root];
        }
        this.renderTree();
    }

    /** The project-first tree: now › setup › basis › phases › deliverables › library. */
    private projectTree(name: string, status: string, cue: Cue, library: NavNode[]): NavNode {
        const ledger = projectStore.ledger;
        const setup = (ledger.setup ?? {}) as Record<string, unknown>;
        const statusPill: NavNode['pill'] = { text: status, status: status === 'published' ? 'available' : status === 'active' ? 'in-test' : 'planned' };
        const bodPill: NavNode['pill'] = { text: cue.bod_status, status: cue.bod_status === 'confirmed' ? 'available' : cue.bod_status === 'none' ? 'planned' : 'in-test' };
        const setupText = setup.facility_id ? 'bound' : (setup.structure_count as number) ? 'authored' : 'empty';
        const root: NavNode = { id: 'root', label: name, depth: 0, service: 'overview', icon: 'fa-folder-open', pill: statusPill, children: [] };
        root.children!.push({
            id: 'now', label: `Now · ${cue.phase_label}`, depth: 1, section: 'cue', icon: 'fa-location-arrow', kind: 'now', reason: cue.summary,
            children: cue.now.map(item => this.cueNode(item, 2)),
        });
        root.children!.push({ id: 'setup', label: 'Setup', depth: 1, service: 'setup', section: 'setup', icon: 'fa-list-check', pill: { text: setupText, status: setupText === 'empty' ? 'planned' : 'available' } });
        root.children!.push({ id: 'bod', label: 'Basis of design', depth: 1, section: 'bod', icon: 'fa-file-signature', pill: bodPill });
        const phases: NavNode = { id: 'phases', label: 'Phases', depth: 1, section: 'phases', icon: 'fa-stairs', children: [] };
        for (const p of cue.phases) {
            if (p.state === 'later') continue;
            const node: NavNode = { id: `phase:${p.id}`, label: p.title, depth: 2, section: 'phases', kind: 'phase' };
            if (p.state === 'past') { node.pill = { text: p.gate?.passed_on ? `passed ${p.gate.passed_on}` : 'passed', status: 'available' }; }
            else if (p.state === 'next') { node.pill = { text: 'next', status: 'planned' }; node.reason = p.entry ?? undefined; }
            else {
                node.pill = { text: `LOD ${p.lod}`, status: 'in-test' };
                node.children = [
                    ...p.evidence_owed.map(f => ({ id: `owed:${f}`, label: `Owed: ${f.replace(/_/g, ' ')}`, depth: 3, section: 'cue' as const })),
                    ...cue.systems_now.map(g => ({
                        id: `now:group:${g.id}`, label: `${g.title} · ${g.phase_label}`, depth: 3, service: g.domain, section: 'systems', level: (g.level ?? undefined) as Applicability | undefined, reason: g.reason,
                        children: g.families.map(f => ({ id: `now:family:${f.id}`, label: f.title, depth: 4, service: g.domain, section: 'systems', level: (f.level ?? undefined) as Applicability | undefined, reason: f.reason })),
                    })),
                ];
            }
            phases.children!.push(node);
        }
        root.children!.push(phases);
        const deliverables = (ledger.deliverables ?? []) as Record<string, unknown>[];
        root.children!.push({
            id: 'deliverables', label: 'Deliverables', depth: 1, section: 'deliverables', icon: 'fa-file-export',
            pill: deliverables.length ? { text: String(deliverables.length), status: 'available' } : undefined,
            children: [
                { id: 'models', label: 'Models', depth: 2, service: 'models', section: 'deliverables', icon: 'fa-cube', reason: 'Upload, normalize and link the models the project carries',
                  pill: Object.keys(ledger.models ?? {}).length ? { text: String(Object.keys(ledger.models ?? {}).length), status: 'available' } : { text: 'none', status: 'planned' } },
                ...deliverables.map((d, i) => ({ id: `deliverable:${i}`, label: String(d.title ?? d.kind ?? 'deliverable'), depth: 2, section: 'deliverables', kind: 'deliverable' as const, reason: String(d.kind ?? '') })),
            ],
        });
        const contracts = Object.entries((ledger.contracts ?? {}) as Record<string, { document_id?: string; ids_id?: string; spec_count?: number; registered?: boolean }>);
        const gates = (ledger.gates ?? {}) as Record<string, { passed?: boolean }>;
        root.children!.push({
            id: 'contracts', label: 'Contracts', depth: 1, section: 'contracts', icon: 'fa-file-contract',
            pill: contracts.length ? { text: String(contracts.length), status: 'available' } : undefined,
            reason: 'The IDS this project carries per phase, built here and checked against its model; registered on the network on purpose',
            children: contracts.map(([tier, c]) => {
                const verdict = gates[tier];
                const pill: NavNode['pill'] = c.registered ? { text: 'registered', status: 'available' }
                    : verdict ? { text: verdict.passed ? 'passed' : 'failed', status: verdict.passed ? 'available' : 'planned' } : { text: 'built', status: 'in-test' };
                return { id: `contract:${tier}`, label: `Tier ${tier} · ${c.spec_count ?? '?'} specs`, depth: 2, section: 'contracts', pill, reason: c.ids_id ?? undefined };
            }),
        });
        const narratives = Object.values((ledger.narratives ?? {}) as Record<string, { zone_count?: number }>);
        const briefed = narratives.reduce((n, x) => n + (x.zone_count ?? 0), 0);
        root.children!.push({
            id: 'narrative', label: 'Zone briefs', depth: 1, service: 'narrative', section: 'cue', icon: 'fa-scroll',
            reason: 'What each zone is for, needs, runs and watches: the Operate-phase declaration, written on the model',
            pill: briefed ? { text: `${briefed} zones`, status: 'available' } : Object.keys(ledger.models ?? {}).length ? { text: 'not written', status: 'planned' } : undefined,
        });
        root.children!.push({ id: 'library', label: 'Library', depth: 1, icon: 'fa-book', collapsed: true, children: library, reason: 'The systems and families the lab offers; what this project calls for is under its current phase' });
        return root;
    }

    private cueNode(item: CueItem, depth: number): NavNode {
        return { id: `now:${item.id}`, label: item.title, depth, service: item.service, section: item.section ?? 'cue', kind: 'now', reason: item.detail, icon: item.skill ? 'fa-wand-magic-sparkles' : item.route ? 'fa-gears' : 'fa-arrow-right' };
    }

    /** The registry tree (Project setup, each domain's systems, phases, types, roles) at a given depth. */
    private libraryNodes(depth: number): NavNode[] {
        const setup = activeSetup();
        const live = hasProjectInputs(setup);
        const { groups } = resolveAll(live ? setup : null);
        const nodes: NavNode[] = [];
        if (!projectStore.project) {
            nodes.push({ id: 'setup', label: 'Project setup', depth, service: 'setup', icon: 'fa-list-check', children: [
                { id: 'setup:bind', label: 'Bind to a network facility', depth: depth + 1, service: 'setup', section: 'setup' },
                { id: 'setup:author', label: 'Parcel and scope', depth: depth + 1, service: 'setup', section: 'setup' },
                { id: 'setup:map', label: 'Permit map', depth: depth + 1, service: 'setup', section: 'setup' },
            ] });
        }
        for (const d of listDomains()) {
            const node: NavNode = { id: `domain:${d.id}`, label: d.id === 'landscape' ? 'Landscape commons' : d.title, depth, service: d.id, swatch: d.accent, icon: d.icon, children: [] };
            node.children!.push({ id: `${d.id}:systems`, label: live ? 'Systems · this project' : 'Systems', depth: depth + 1, service: d.id, section: 'systems',
                children: groups.filter(g => g.domain === d.id).map(g => ({
                    id: `${d.id}:group:${g.id}`, label: `${g.title} · ${g.phase_label}`, depth: depth + 2, service: d.id, section: 'systems',
                    level: live ? g.level : undefined,
                    reason: `${g.ifc.class}${g.ifc.predefined_type ? ` (${g.ifc.predefined_type})` : ''}${g.material_tags.length ? ` · ${g.material_tags.join(', ')}` : ''}${live ? ` — ${g.reason}` : ''}`,
                    children: g.families.map(f => ({
                        id: `${d.id}:family:${f.id}`, label: f.title, depth: depth + 3, service: d.id, section: 'systems', status: f.status,
                        level: live ? f.level : undefined, reason: `${f.ifc_class} · ${f.phase_label}${live ? ` — ${f.reason}` : ''}`,
                    })),
                })) });
            node.children!.push({ id: `${d.id}:phases`, label: 'Phases', depth: depth + 1, service: d.id, section: 'phases',
                children: d.phases.map(p => ({ id: `${d.id}:phase:${p.id}`, label: `${p.title} · ${p.lod}`, depth: depth + 2, service: d.id, section: 'phases', status: p.status })) });
            node.children!.push({ id: `${d.id}:types`, label: d.id === 'building' ? 'Building types' : 'Site types', depth: depth + 1, service: d.id, section: 'types',
                children: listCommunityTypes(d.id).map(t => ({ id: `${d.id}:type:${t.id}`, label: t.title, depth: depth + 2, service: d.id, section: 'types', status: t.status })) });
            node.children!.push({ id: `${d.id}:roles`, label: 'Roles', depth: depth + 1, service: d.id, section: 'roles' });
            nodes.push(node);
        }
        return nodes;
    }

    toggleLeft(): void { this.state.left = this.state.left === 'open' ? 'collapsed' : 'open'; this.persist(); this.applyState(); }
    toggleRight(): void { this.setRightMode(this.state.right === 'hidden' ? (window.innerWidth < 1100 ? 'floating' : 'pinned') : 'hidden'); }
    setRightMode(mode: RightMode): void { this.state.right = mode; this.persist(); this.applyState(); }
    isRightVisible(): boolean { return this.state.right !== 'hidden'; }

    /** Open the Info panel on a section (used by "Details" buttons in the view). */
    showInfo(section?: string): void {
        if (this.state.right === 'hidden') this.setRightMode(window.innerWidth < 1100 ? 'floating' : 'pinned');
        if (section) this.info.focus(section);
    }

    // ── Internals ─────────────────────────────────────────────────────

    private wireChrome(): void {
        this.left.querySelector('#ws-left-collapse')?.addEventListener('click', () => this.toggleLeft());
        this.right.querySelector('#ws-right-close')?.addEventListener('click', () => this.setRightMode('hidden'));
        this.right.querySelector('#ws-right-pin')?.addEventListener('click', () =>
            this.setRightMode(this.state.right === 'pinned' ? 'floating' : 'pinned'));
        this.wireGutter(this.gutterL, 'left');
        this.wireGutter(this.gutterR, 'right');
        this.leftBody.addEventListener('click', (e) => {
            const nodeEl = (e.target as HTMLElement).closest<HTMLElement>('.ws-node');
            if (!nodeEl) return;
            const id = nodeEl.dataset.id!;
            if ((e.target as HTMLElement).closest('.ws-caret')) { nodeEl.classList.toggle('collapsed-children'); return; }
            const node = this.findNode(id);
            if (!node) return;
            this.selectNode(id);
            if (node.service) this.onNavigate(node.service);
            if (node.section) this.showInfo(node.section);
        });
    }

    private wireGutter(gutter: HTMLElement, side: 'left' | 'right'): void {
        gutter.addEventListener('pointerdown', (down) => {
            down.preventDefault();
            gutter.classList.add('dragging');
            const startX = down.clientX;
            const startW = side === 'left' ? this.state.leftW : this.state.rightW;
            const move = (ev: PointerEvent) => {
                const delta = ev.clientX - startX;
                if (side === 'left') this.state.leftW = clamp(startW + delta, MIN_LEFT, MAX_LEFT);
                else this.state.rightW = clamp(startW - delta, MIN_RIGHT, MAX_RIGHT);
                this.applyWidths();
            };
            const up = () => {
                gutter.classList.remove('dragging');
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
                this.persist();
            };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
        });
    }

    private applyWidths(): void {
        document.documentElement.style.setProperty('--ws-left-w', `${this.state.leftW}px`);
        document.documentElement.style.setProperty('--ws-right-w', `${this.state.rightW}px`);
    }

    private applyState(): void {
        this.applyWidths();
        this.left.classList.toggle('collapsed', this.state.left !== 'open');
        this.gutterL.classList.toggle('hidden', this.state.left !== 'open');
        this.right.classList.toggle('collapsed', this.state.right === 'hidden');
        this.right.classList.toggle('floating', this.state.right === 'floating');
        this.gutterR.classList.toggle('hidden', this.state.right !== 'pinned');
        this.right.querySelector('#ws-right-pin')?.classList.toggle('active', this.state.right === 'pinned');
        document.getElementById('ws-toggle-left')?.classList.toggle('active', this.state.left === 'open');
        document.getElementById('info-modal-btn')?.classList.toggle('active', this.state.right !== 'hidden');
    }

    private persist(): void {
        try { localStorage.setItem(STORE_KEY, JSON.stringify(this.state)); } catch { /* ignore */ }
    }

    private renderStatus(ctx: WorkspaceContext): void {
        const domain = ctx.domain ? listDomains().find(d => d.id === ctx.domain) : null;
        const crumbs = domain ? `<b>${esc(PLATFORM.name)}</b> › ${esc(domain.title)}` : `<b>${esc(PLATFORM.name)}</b>`;
        const hint = ctx.hint ?? (domain ? domain.statusHint : PLATFORM.pattern.map(s => s.title).join(' · '));
        this.status.innerHTML = `
            <span class="ws-crumbs">${crumbs}</span>
            <span class="ws-hint">${esc(hint)}</span>
            <span class="ws-right">
                <span>IFC 4.3 · IDS</span>
                <a href="${NETWORK.site}" target="_blank" rel="noopener">mokunet.org</a>
                <a href="${NETWORK.platform}" target="_blank" rel="noopener">platform</a>
                <a href="https://ainadesign.com" target="_blank" rel="noopener">Āina Design Corp</a>
            </span>`;
    }

    private renderTree(): void {
        const pill = (n: NavNode): string => n.level ? `<span class="ws-pill" data-level="${n.level}">${LEVEL_LABEL[n.level]}</span>`
            : n.status ? `<span class="ws-pill" data-status="${n.status}">${STATUS_LABEL[n.status]}</span>`
            : n.pill ? `<span class="ws-pill" data-status="${n.pill.status}">${esc(n.pill.text)}</span>` : '';
        const render = (nodes: NavNode[]): string => nodes.map(n => `
            <div class="ws-node ${n.level === 'not_applicable' ? 'ws-node-na' : ''} ${n.collapsed ? 'collapsed-children' : ''}" data-id="${esc(n.id)}" data-depth="${n.depth}" ${n.kind ? `data-kind="${n.kind}"` : ''} title="${esc(n.reason ? `${n.label} — ${n.reason}` : n.label)}" ${n.swatch ? `style="--swatch:${n.swatch}"` : ''}>
                <span class="ws-caret">${n.children && n.children.length ? '<i class="fa-solid fa-caret-down"></i>' : ''}</span>
                ${n.swatch ? '<span class="ws-swatch"></span>' : (n.icon ? `<i class="fa-solid ${n.icon}" style="font-size:10px;color:var(--ws-text-faint)"></i>` : '')}
                <span class="ws-node-label">${esc(n.label)}</span>
                ${pill(n)}
            </div>
            ${n.children && n.children.length ? `<div class="ws-children">${render(n.children)}</div>` : ''}
        `).join('');
        this.leftBody.innerHTML = `<div class="ws-tree">${render(this.tree)}</div>`;
        if (this.selectedNode) this.selectNode(this.selectedNode);
    }

    private selectNode(id: string): void {
        this.selectedNode = id;
        this.leftBody.querySelectorAll('.ws-node').forEach(n => n.classList.toggle('selected', (n as HTMLElement).dataset.id === id));
    }

    private findNode(id: string, nodes: NavNode[] = this.tree): NavNode | null {
        for (const n of nodes) {
            if (n.id === id) return n;
            const hit = n.children ? this.findNode(id, n.children) : null;
            if (hit) return hit;
        }
        return null;
    }
}

function el(tag: string, cls: string): HTMLElement {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    return e;
}
function clamp(v: number, lo: number, hi: number): number { return Math.min(hi, Math.max(lo, v)); }
function esc(s: string): string {
    return s.replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));
}
